import { defineComponent } from 'vue';
import Trailer from '@/types/Trailer';
import { findTrailerById } from '@/shared/trailerLookup';

interface Data {
    trailer: Trailer | null;
    notFound: boolean;
}

export default defineComponent({
    props: {
        trailerNumber: {
            type: String,
            required: true,
        },
        // Screens that can show a trailer from another company (the Virtual Red Tag
        // unavailable screen) must look it up by its own company, not the driver's,
        // or a same-numbered trailer in the driver's fleet comes back instead.
        trailerCompany: {
            type: String,
            default: undefined,
        },
    },
    data(): Data {
        return {
            trailer: null,
            notFound: false,
        };
    },
    computed: {
        driverCompany(): string {
            return this.$route.params.company as string;
        },
        // The trailer's type ("Dry Van", "Refrigerated"), or a generic "Trailer" when
        // there isn't one. A trailer selected via QR scan never carries a type, so
        // captions would otherwise read as just "#123456".
        trailerTypeLabel(): string {
            return this.trailer?.type?.trim() || 'Trailer';
        },
        lookupCompany(): string {
            // Truthiness, not ??: the override arrives from a query string, so it can be
            // null or empty as easily as undefined.
            return this.trailerCompany || this.driverCompany;
        },
    },
    methods: {
        async fetchTrailerByNumber(): Promise<void> {
            const selectedTrailer = this.$store.getters.selectedTrailer as
                Trailer | undefined;

            const selectedTrailerMatches =
                selectedTrailer?.trailerNumber === this.trailerNumber &&
                // A trailer selected via QR scan (Trailer.mapFromITrailerQrDto) always
                // has an empty type, since the QR lookup's DTO never carries one. Trust
                // the store entity only when it actually has a type to show, or screens
                // reached from a QR scan render "#123456" with no trailer type at all.
                Boolean(selectedTrailer?.type) &&
                (!this.trailerCompany ||
                    selectedTrailer?.company?.toUpperCase() ===
                        this.trailerCompany.toUpperCase());

            if (selectedTrailer && selectedTrailerMatches) {
                this.trailer = selectedTrailer;
                return;
            }

            // Every screen using this mixin is a Virtual Red Tag screen, where
            // eligibility (Trailer.TypeClassification) allows Refrigerated trailers,
            // not just Dry Van.
            const result = await findTrailerById(
                this.trailerNumber,
                this.lookupCompany,
                true
            );

            if (result) {
                this.trailer = result;
            } else {
                this.notFound = true;
            }
        },
    },
});
